// ─────────────────────────────────────────────────────────────────────────────
// CustomizeSheetContent.tsx — "Customize" mobile sheet: colour scheme + layout
//
// Mobile equivalent of ThemeSelector.tsx's desktop dropdown content. Reuses
// the exact same data/actions — only the touch-target sizing differs (44px
// swatches here vs. 28px on desktop, since this sheet has the full screen
// width to work with rather than a narrow dropdown).
// ─────────────────────────────────────────────────────────────────────────────

import { Palette, LayoutGrid, Sun, Moon, Monitor } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setColourScheme } from '@/store/uiSlice';
import { useLayoutSelect } from '@/hooks/useLayoutSelect';
import { useTheme } from '@/hooks/useTheme';
import { COLOUR_SCHEMES } from '@/styles/colourSchemes';
import { LAYOUTS } from '@/data/navOptions';

export function CustomizeSheetContent() {
  const dispatch     = useAppDispatch();
  const activeScheme = useAppSelector((s) => s.ui.colourScheme);
  const activeLayout = useAppSelector((s) => s.ui.cvLayout);
  const selectLayout  = useLayoutSelect();
  const { mode, changeTheme } = useTheme();

  const activeSchemeObj = COLOUR_SCHEMES.find((s) => s.id === activeScheme);

  const THEME_MODES = [
    { value: 'light' as const,  label: 'Light',  icon: Sun },
    { value: 'dark' as const,   label: 'Dark',   icon: Moon },
    { value: 'system' as const, label: 'System', icon: Monitor },
  ];

  return (
    <div className="pb-4">
      {/* ── Light / Dark / System ─────────────────────────────────────── */}
      {/* Moved here from the old mobile top bar — that bar was removed
          entirely on mobile (more screen space), and this is an
          "appearance" control, so it belongs grouped with colour/layout. */}
      <div className="p-3">
        <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-3 flex items-center gap-1.5">
          <Sun size={12} />
          Theme
        </p>
        <div className="grid grid-cols-3 gap-2">
          {THEME_MODES.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => changeTheme(value)}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-colors
                ${mode === value
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-transparent hover:bg-slate-50 dark:hover:bg-navy-800 text-slate-600 dark:text-slate-300'
                }`}
              aria-pressed={mode === value}
            >
              <Icon size={18} />
              <span className="text-xs font-medium">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Colour Schemes ─────────────────────────────────────────────── */}
      <div className="p-3">
        <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-3 flex items-center gap-1.5">
          <Palette size={12} />
          Colour Scheme
        </p>

        <div className="grid grid-cols-5 gap-3 px-1">
          {COLOUR_SCHEMES.map((scheme) => (
            <button
              key={scheme.id}
              onClick={() => dispatch(setColourScheme(scheme.id))}
              title={scheme.name}
              aria-label={scheme.name}
              aria-pressed={activeScheme === scheme.id}
              className={`
                w-11 h-11 rounded-full border-2 transition-all duration-150
                ${activeScheme === scheme.id
                  ? 'border-white shadow-lg scale-110 ring-2 ring-offset-1 ring-slate-300 dark:ring-navy-400'
                  : 'border-transparent opacity-75'
                }
              `}
              style={{
                background: `linear-gradient(135deg, ${scheme.hero} 50%, ${scheme.accent} 50%)`,
              }}
            />
          ))}
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 px-1 font-medium">
          {activeSchemeObj?.name ?? ''}
        </p>
      </div>

      {/* ── Page Layout ────────────────────────────────────────────────── */}
      <div className="border-t border-slate-200 dark:border-navy-700 p-2 mt-1">
        <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider px-3 py-2 flex items-center gap-1.5">
          <LayoutGrid size={12} />
          Page Layout
        </p>

        {LAYOUTS.map((layout) => (
          <button
            key={layout.value}
            onClick={() => selectLayout(layout)}
            className={`
              w-full text-left px-3 py-3 rounded-xl transition-colors flex items-start gap-3
              ${activeLayout === layout.value
                ? 'bg-accent-soft text-accent'
                : 'hover:bg-slate-50 dark:hover:bg-navy-800 text-slate-700 dark:text-slate-300'
              }
            `}
            role="option"
            aria-selected={activeLayout === layout.value}
          >
            <span className="mt-0.5 text-sm w-5 text-center font-mono shrink-0">
              {layout.icon}
            </span>
            <span>
              <span className="block text-sm font-medium leading-tight">{layout.label}</span>
              <span className="block text-xs text-slate-400 dark:text-slate-500 mt-0.5">{layout.desc}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
